import 'src/lib/polyfills';

import React, { useMemo } from 'react';
import { compose } from 'recompose';
import { ApolloProvider } from '@apollo/react-hooks';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { CompatRouter } from 'react-router-dom-v5-compat';

import wrapWith from 'src/utils/wrapWith';
import ErrorBoundary from 'src/services/ErrorBoundary';
import { LocaleContext } from 'src/utils/T';
import ThemeAndStylesProvider from './components/ThemeAndStylesProvider';
import { createAppTheme } from './views/theme';
import locale from './locale/en';
import apolloClient from './store/client';
import GlobalStoreContainers from './store/GlobalStoreContainers';
import NotificationsService from './services/notifications';
import { PopupService } from './services/popup';
import { useApplicationMode } from './services';
import BuildRelevanceChecker from './services/BuildRelevanceChecker';
import CustomerSelectionSaver from './services/CustomerSelectionSaver';
import UserAnalyticsUpdater from './services/UserAnalyticsUpdater';
import AuthorCompleteTracer from './services/AuthorCompleteTracer';
import ZendeskSupportWidget from './services/ZendeskSupportWidget';
import { useSupportInfinityExtension } from './services/infinity';
import { AppRouting } from './App.Routing';
import AuthTokenGuard from './services/AuthTokenGuard';
import { BrandRulesGuard } from 'src/services/BrandRulesGuard';
import { OnboardingRoot } from './views/OnboardingRoot';
import { OnboardingRouteGuard } from './services/OnboardingRouteGuard';
import { PopupNotifications } from './views/NotificationsCenter';
import { LocationGuard } from './services/LocationGuard';
import { BackendDataUpdater } from './services/BackendDataUpdater';
import { ConfirmationDialog } from './views/ConfirmationDialog';
import {
  CustomerSubscriptionPausedGuard,
  UserGuard,
  useImpactClickId,
  useMaintainPerformanceStoreState,
} from 'src/services';
import { isExternalToolRoute } from 'src/lib/routing';

const AppThemeProvider = (props) => {
  const applicationMode = useApplicationMode();
  const theme = useMemo(() => createAppTheme({ applicationMode }), [applicationMode]);

  return (
    <ThemeAndStylesProvider
      theme={theme}
      {...props}
    />
  );
};

const App = () => {
  useMaintainPerformanceStoreState();
  useSupportInfinityExtension();
  useImpactClickId();

  return (
    <>
      <Helmet
        titleTemplate="%s - Anyword AI Copywriter"
        defaultTitle="Anyword AI Copywriter"
      />

      <Router>
        <CompatRouter>
          <PopupService>
            <NotificationsService />

            <BuildRelevanceChecker />
            <CustomerSelectionSaver />
            <UserAnalyticsUpdater />
            <AuthorCompleteTracer />
            <ZendeskSupportWidget />

            {/* Disable all guards for external tools routes */}
            {!isExternalToolRoute() && (
              <>
                <AuthTokenGuard />
                <BrandRulesGuard />
                <BackendDataUpdater />
                <UserGuard />
                <CustomerSubscriptionPausedGuard />
                <LocationGuard />
                <OnboardingRoot />
                <OnboardingRouteGuard />
              </>
            )}

            <LastLocationProvider>
              <PopupNotifications />
              <ConfirmationDialog />

              <AppRouting />
            </LastLocationProvider>
          </PopupService>
        </CompatRouter>
      </Router>
    </>
  );
};

export default compose(
  wrapWith(ErrorBoundary),
  wrapWith(HelmetProvider),
  wrapWith(LocaleContext.Provider, { value: locale }),
  wrapWith(ApolloProvider, { client: apolloClient }),
  wrapWith(GlobalStoreContainers),
  wrapWith(AppThemeProvider)
)(App);
